import { certaintyPercent } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber } from '../components/data/data-text';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { LearnMore } from '../components/guidance/learn-more';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// Admin → Matching (D418, the owner's rule of 2026-10-20): every proposed entity match carries a certainty; only a
// match at or above this threshold is applied without a person (a dataset row attached to an existing entity, an
// auto-match when a review is skipped). Everything below waits in the Review Inbox as a "Same entity?" question.

export const Route = createFileRoute('/admin/matching')({ component: MatchingPage });

interface Matching {
  threshold: number;
  defaultThreshold: number;
  min: number;
  max: number;
}

const THRESHOLD_HELP =
  'A match at or above this certainty is applied without a person: a dataset row is added to the existing entity, and a skipped review attaches the name to it (to confirm later). Anything below is kept separate and asked in the Review Inbox. Names in two scripts, aliases, acronyms and surnames alone are always asked, whatever the threshold.';

function MatchingPage() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const id = useId();
  const q = useQuery({ queryKey: ['admin', 'matching'], queryFn: () => api<Matching>('/admin/matching') });
  const [text, setText] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (q.data) setText(q.data.threshold.toFixed(2));
  }, [q.data]);
  const save = useMutation({
    mutationFn: (threshold: number) => withReauth(() => api<Matching>('/admin/matching', { method: 'PUT', body: { threshold } })),
    onSuccess: m => {
      qc.setQueryData(['admin', 'matching'], m);
      setMessage({
        kind: 'ok',
        text: `Saved. Matches at ${certaintyPercent(m.threshold)} certainty or more are applied without a person from the next import or skipped review.`,
      });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong. Try again.' });
    },
  });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.data) return;
    setMessage(null);
    const raw = text.trim().replace(',', '.');
    const v = Number(raw.endsWith('%') ? Number(raw.slice(0, -1)) / 100 : raw);
    if (!raw || !Number.isFinite(v) || v < q.data.min || v > q.data.max)
      return setMessage({
        kind: 'error',
        text: `Enter a certainty from ${q.data.min.toFixed(2)} to ${q.data.max.toFixed(2)} (for example ${q.data.defaultThreshold.toFixed(2)}).`,
      });
    save.mutate(Math.round(v * 100) / 100);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Matching"
        description="How certain Hoopoe must be that two records are the same entity before it links them without asking a person."
      />
      {q.isError ? (
        <ErrorState
          error={q.error}
          title="The matching settings couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="match-certainty"
        />
      ) : !q.data ? (
        <div aria-busy="true" className="h-24 animate-pulse rounded-md bg-muted">
          <span className="sr-only">Loading…</span>
        </div>
      ) : (
        <section aria-labelledby="matching-threshold" className="max-w-2xl space-y-4 rounded-lg border bg-background p-4">
          <div className="flex items-center gap-1">
            <h2 id="matching-threshold" className="text-base font-semibold">
              Match certainty threshold
            </h2>
            <FieldHelp label="Match certainty threshold" help={THRESHOLD_HELP} learnMore="match-certainty" />
          </div>
          <p className="text-sm">
            Now: <DataNumber>{q.data.threshold.toFixed(2)}</DataNumber> ({certaintyPercent(q.data.threshold)}). The default is{' '}
            <DataNumber>{q.data.defaultThreshold.toFixed(2)}</DataNumber>.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <label htmlFor={id} className="text-sm font-medium">
                Apply a match without a person at this certainty or more
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id={id}
                  inputMode="decimal"
                  className="w-28"
                  value={text}
                  onChange={e => setText(e.target.value)}
                  aria-describedby={`${id}-hint`}
                />
                <span id={`${id}-hint`} className="text-xs text-muted-foreground">
                  from {q.data.min.toFixed(2)} to {q.data.max.toFixed(2)}; 1.00 sends every match to review
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={save.isPending}>
                <Save aria-hidden="true" /> {save.isPending ? 'Saving…' : 'Save threshold'}
              </Button>
              {message && (
                <p
                  role={message.kind === 'error' ? 'alert' : 'status'}
                  className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
                >
                  {message.text}
                </p>
              )}
            </div>
          </form>
          <p className="text-sm text-muted-foreground">
            Matches below the threshold wait under <span className="font-medium text-foreground">Possible matches</span> in the{' '}
            <Link to="/review" className="underline underline-offset-2">
              Review Inbox
            </Link>
            , best first, each with its certainty and the reasons for it. Merging two existing entities is always a person’s answer.
          </p>
          <LearnMore slug="match-certainty" />
        </section>
      )}
    </div>
  );
}
